import { ParkingtonTool, type ParkingtonState } from '../ParkingtonTypes';
import type { InputCallbacks } from './InputTypes';

/**
 * Handles keyboard shortcuts for the Parkington layer.
 */
export class KeyboardHandler {
  handleKeyDown(e: KeyboardEvent, state: ParkingtonState, callbacks: InputCallbacks): void {
    if (state.inputBlocked) return;

    // Tool shortcuts
    if (e.key === 's' || e.key === 'S') {
      callbacks.setTool(ParkingtonTool.SELECT);
      return;
    } else if (e.key === 'p' || e.key === 'P') {
      if (state.precisionUnlocked) {
        callbacks.setTool(ParkingtonTool.PRECISION);
      }
      return;
    } else if (e.key === 'h' || e.key === 'H') {
      callbacks.setTool(ParkingtonTool.HIGHWAY);
      return;
    } else if (e.key === 'c' || e.key === 'C') {
      // C for covenant, B for the bushes, U for the way under. The three mercy
      // builds get plain letters in the same block as the road brush and no
      // qualifier of any kind, which is the interface saying what §10 says: these
      // are ordinary construction items that happen to pay nothing.
      //
      // G and F are deliberately left alone. §12 spends both — City Hall and the
      // federal overlay — and taking one now would cost a rebinding in Phase 4.
      callbacks.setTool(ParkingtonTool.COVENANT);
      return;
    } else if (e.key === 'b' || e.key === 'B') {
      callbacks.setTool(ParkingtonTool.HEDGEROW);
      return;
    } else if (e.key === 'u' || e.key === 'U') {
      callbacks.setTool(ParkingtonTool.UNDERCROSS);
      return;
    } else if (e.key === 'i' || e.key === 'I') {
      // I for the interchange, in the same block and with the same absence of
      // qualifier. It is an ordinary construction item that happens to connect the
      // district to a national road network nobody in this build can see.
      callbacks.setTool(ParkingtonTool.INTERCHANGE);
      return;
    } else if (e.key === 'm' || e.key === 'M') {
      callbacks.togglePanMode();
      return;
    } else if (e.key === 'r' || e.key === 'R') {
      // R for the report, and it is not a tool: it selects nothing, builds
      // nothing and puts a document on screen. In the tool block anyway because
      // that is the block a player's fingers are in, and next to nothing else
      // because §12 spends G on City Hall and F on the federal overlay.
      callbacks.toggleSeasonReport();
      return;
    } else if (e.key === 'Escape') {
      // A card on screen is the outermost thing to dismiss, before a selection:
      // a mayor who has just been handed a settlement and presses Escape means
      // the settlement.
      if (state.seasonReportShown) {
        callbacks.toggleSeasonReport();
        return;
      }
      if (state.selectedEntity) {
        callbacks.setSelectedEntity(null);
        return;
      }
    }

    if (e.key === 't' || e.key === 'T') {
      callbacks.toggleShowPheromones();
    } else if (e.key === 'w' || e.key === 'W') {
      // W for walkout. The mayor's labour verb, aimed at whatever the cursor is
      // over — there is no other way to name a facility until City Hall arrives,
      // and a facility is the only thing on the map a mayor can address.
      if (state.hoveredCell) {
        callbacks.toggleStaffing(state.hoveredCell.x, state.hoveredCell.y);
      }
    } else if (e.key === ' ' || e.code === 'Space') {
      e.preventDefault();
      const currentScale = state.camera.getScale();
      if (currentScale > 1.1) {
        state.camera.zoomAtPoint(state.mouseX, state.mouseY, 1 / currentScale);
      } else {
        state.camera.zoomAtPoint(state.mouseX, state.mouseY, 2); // SPACE_ZOOM_FACTOR
      }
    }
  }

  handleKeyUp(e: KeyboardEvent, state: ParkingtonState, callbacks: InputCallbacks): void {
    // Reserved for future key-up events
  }
}
